import { useState } from 'react'
import { router } from '@inertiajs/react'
import {
    Box,
    Typography,
    Divider,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Button,
    Snackbar,
    Alert
} from '@mui/material'
import {
    InfoOutlined as InfoIcon,
    East as EastIcon,
    Close as CloseIcon,
    ContentCopy as CopyIcon,
    Support as SupportIcon
} from '@mui/icons-material'
import { CustomCard, PrimaryButton, SecondaryButton } from '../index'
import ProspectTourAvailabilityOverlay from './ProspectTourAvailabilityOverlay'
import dogRunningImage from '../../assets/dog_running.png'
import { useAuth } from '../../hooks/useAuth'
import axiosClient from '../../services/axiosClient'

interface HomeTabProps {
    pending_tour_requests_count?: number;
    upcoming_tours_count?: number;
    prospect_short_code?: string;
    prospect_id?: string;
}

export default function HomeTab({
    pending_tour_requests_count = 0,
    upcoming_tours_count = 0,
    prospect_short_code,
    prospect_id
}: HomeTabProps) {
    const { renter } = useAuth();
    // State for tour availability overlay
    const [availabilityOpen, setAvailabilityOpen] = useState(false)
    // State for support overlay
    const [supportOpen, setSupportOpen] = useState(false)
    const [snackbarOpen, setSnackbarOpen] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState('')
    // State for found home overlay
    const [foundHomeOpen, setFoundHomeOpen] = useState(false)


    const handleFoundHome = () => {
        setFoundHomeOpen(true)
    }

    const handleCloseFoundHome = () => {
        setFoundHomeOpen(false)
    }

    const handleFoundHomeResponse = async (foundUsingService: boolean) => {
        try {
            if (!prospect_id) {
                console.error('Missing prospect_id')
                return
            }

            await axiosClient.post(`/renters/${prospect_id}/complete_search`, {
                found_home_using_service: foundUsingService
            })

            setSnackbarMessage('Thanks! We updated your search status.')
            setSnackbarOpen(true)
        } catch (err) {
            console.error('Failed to update search status', err)
            setSnackbarMessage('Failed to update status. Please try again.')
            setSnackbarOpen(true)
        } finally {
            setFoundHomeOpen(false)
        }
    }

    const handleOpenAvailability = () => {
        setAvailabilityOpen(true)
    }

    const handleCloseAvailability = () => {
        setAvailabilityOpen(false)
    }

    const handleAvailabilitySuccess = () => {
        // Handle successful availability update
        console.log('Tour availability updated successfully')
        setAvailabilityOpen(false)
    }

    const handleOpenSupport = () => {
        setSupportOpen(true)
    }

    const handleCloseSupport = () => {
        setSupportOpen(false)
    }

    const handleCopyToClipboard = async (text: string, label: string) => {
        try {
            await navigator.clipboard.writeText(text)
            setSnackbarMessage(`${label} copied to clipboard!`)
            setSnackbarOpen(true)
        } catch (err) {
            console.error('Failed to copy text: ', err)
            setSnackbarMessage('Failed to copy to clipboard')
            setSnackbarOpen(true)
        }
    }

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false)
    }

    // Get the prospect identifier (short code preferred, fallback to ID)
    const prospectIdentifier = prospect_short_code || prospect_id || 'N/A'

    return (
        <Box>
            {/* Profile Status Card */}
            <CustomCard>
                <Box sx={{ p: 1.5 }}>
                    {/* Status Section */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        {/* Dog Image */}
                        <Box>
                            <img
                                src={dogRunningImage}
                                alt="Dog hunting"
                                style={{
                                    width: '110px',
                                    height: 'auto',
                                    objectFit: 'contain'
                                }}
                            />
                        </Box>

                        {/* Status Message */}
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                Hunting
                            </Typography>
                        </Box>
                    </Box>

                    <Typography variant="body2" sx={{ fontSize: 14, color: 'grey.600', textAlign: 'center'}}>
                        Messaging landlords.
                    </Typography>

                    {/* Divider */}
                    <Divider sx={{ mb: 2 }} />

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <PrimaryButton
                            fullWidth
                            onClick={handleFoundHome}
                        >
                            I found a home
                        </PrimaryButton>
                    </Box>
                </Box>
            </CustomCard>

            {/* Pending Tour Requests Card */}


            {pending_tour_requests_count > 0 && (
                <CustomCard sx={{ mt: 3 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box display="flex" alignItems="baseline" gap={1}>
                                <Typography fontSize="35px" fontWeight="bold">
                                    {pending_tour_requests_count}
                                </Typography>
                                <Typography fontSize="17.5px">Pending Requests</Typography>
                            </Box>
                            <EastIcon
                                sx={{ cursor: 'pointer', color: 'black' }}
                                onClick={() => router.visit(`/renters/${renter?.id}/tours`)}
                            />
                        </Box>
                    </CardContent>
                </CustomCard>
            )}

            {/* Upcoming Tours Card */}
            {upcoming_tours_count > 0 && (
                <CustomCard sx={{ mt: 3 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box display="flex" alignItems="baseline" gap={1}>
                                <Typography fontSize="35px" fontWeight="bold">
                                    {upcoming_tours_count}
                                </Typography>
                                <Typography fontSize="17.5px">Upcoming Tours</Typography>
                            </Box>
                            <EastIcon
                                sx={{ cursor: 'pointer', color: 'black' }}
                                onClick={() => router.visit(`/renters/${renter?.id}/tours?tab=scheduled`)}
                            />
                        </Box>
                    </CardContent>
                </CustomCard>
            )}


            {/* Tour Availability Card */}
            <CustomCard sx={{ mt: 3 }}>
                <Box sx={{ p: 3 }}>
                    <Box display="flex" alignItems="center" mb={1} gap={1}>
                        <InfoIcon sx={{ color: 'grey.400' }} />
                        <Typography fontWeight="bold">Update your tour availability</Typography>
                    </Box>
                    <Typography variant="body2" mb={3}>
                        Let landlords know when you're available for tours.
                    </Typography>
                    <SecondaryButton
                        fullWidth
                        onClick={handleOpenAvailability}
                    >
                        Update availability
                    </SecondaryButton>
                </Box>
            </CustomCard>

            {/* Contact Support Card */}
            <CustomCard sx={{ mt: 3 }}>
                <Box sx={{ p: 3 }}>
                    <Box display="flex" alignItems="center" mb={1} gap={1}>
                        <SupportIcon sx={{ color: 'grey.400' }} />
                        <Typography fontWeight="bold">Need Help?</Typography>
                    </Box>
                    <Typography variant="body2" mb={3}>
                        Contact our support team for assistance.
                    </Typography>
                    <SecondaryButton
                        fullWidth
                        onClick={handleOpenSupport}
                    >
                        Contact Support
                    </SecondaryButton>
                </Box>
            </CustomCard>

            {/* Tour Availability Overlay */}
            <ProspectTourAvailabilityOverlay
                open={availabilityOpen}
                onClose={handleCloseAvailability}
                onSuccess={handleAvailabilitySuccess}
                prospectId={prospect_id}
            />

            {/* Support Dialog */}
            <Dialog
                open={supportOpen}
                onClose={handleCloseSupport}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: '16px' }
                }}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pb: 1
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Contact Support
                    </Typography>
                    <IconButton onClick={handleCloseSupport} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                            Text or email us for assistance:
                        </Typography>

                        {/* Support Email */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 2,
                            border: '1px solid',
                            borderColor: 'grey.300',
                            borderRadius: '8px',
                            mb: 2
                        }}>
                            <Box>
                                <Typography variant="body2" color="grey.600" sx={{ mb: 0.5 }}>
                                    Email
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    rich@heyyproperty.com
                                </Typography>
                            </Box>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<CopyIcon />}
                                onClick={() => handleCopyToClipboard('rich@heyyproperty.com', 'Email')}
                                sx={{ minWidth: 'auto', px: 2 }}
                            >
                                Copy
                            </Button>
                        </Box>

                        {/* Support Phone */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 2,
                            border: '1px solid',
                            borderColor: 'grey.300',
                            borderRadius: '8px',
                            mb: 3
                        }}>
                            <Box>
                                <Typography variant="body2" color="grey.600" sx={{ mb: 0.5 }}>
                                    Phone
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    617-459-2265
                                </Typography>
                            </Box>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<CopyIcon />}
                                onClick={() => handleCopyToClipboard('617-459-2265', 'Phone number')}
                                sx={{ minWidth: 'auto', px: 2 }}
                            >
                                Copy
                            </Button>
                        </Box>

                        {/* Prospect ID Section */}
                        <Box sx={{
                            p: 2,
                            bgcolor: 'grey.50',
                            borderRadius: '8px',
                            border: '1px solid',
                            borderColor: 'grey.200'
                        }}>
                            <Typography variant="body2" color="grey.600" sx={{ mb: 1 }}>
                                Your Prospect ID
                            </Typography>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                <Typography variant="body1" sx={{
                                    fontWeight: 500,
                                    fontFamily: 'monospace',
                                    fontSize: '0.9rem'
                                }}>
                                    {prospectIdentifier}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<CopyIcon />}
                                    onClick={() => handleCopyToClipboard(prospectIdentifier, 'Prospect ID')}
                                    sx={{ minWidth: 'auto', px: 2 }}
                                >
                                    Copy
                                </Button>
                            </Box>
                            <Typography variant="body2" color="grey.600" sx={{ mt: 1, fontSize: '0.8rem' }}>
                                When you contact support, give us your ID so we can find you in our system.
                            </Typography>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Found Home Dialog */}
            <Dialog
                open={foundHomeOpen}
                onClose={handleCloseFoundHome}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: '16px' }
                }}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pb: 1
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        🎉 Congratulations!
                    </Typography>
                    <IconButton onClick={handleCloseFoundHome} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
                            Did you find your home using our service?
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                            <PrimaryButton
                                onClick={() => handleFoundHomeResponse(true)}
                                sx={{ minWidth: '120px' }}
                            >
                                Yes
                            </PrimaryButton>
                            <SecondaryButton
                                onClick={() => handleFoundHomeResponse(false)}
                                sx={{ minWidth: '120px' }}
                            >
                                No
                            </SecondaryButton>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Snackbar for copy feedback */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    )
}
